// Phone number display formats per country code
// '#' = digit placeholder. The format applies to the LOCAL part only (after country code).
// Multiple formats separated by '|' for countries with variable-length numbers.
// The formatter picks the best-fitting pattern based on digit count.

export interface PhoneFormat {
  pattern: string;    // e.g. "### ### ####" or "## #### ####|### ### ####"
  trunk?: string;     // trunk prefix stripped when dialing internationally (e.g. "0")
  example?: string;   // example local number (digits only)
}

export const PHONE_FORMATS: Record<string, PhoneFormat> = {
  // Americas - North
  US: { pattern: "(###) ###-####", example: "2025551234" },
  CA: { pattern: "(###) ###-####", example: "6135551234" },
  MX: { pattern: "## #### ####", trunk: "1", example: "5512345678" },

  // Americas - Caribbean & Central (NANP countries share US format)
  AG: { pattern: "(###) ###-####", example: "2684601234" },
  AI: { pattern: "(###) ###-####", example: "2644611234" },
  BB: { pattern: "(###) ###-####", example: "2462501234" },
  BM: { pattern: "(###) ###-####", example: "4412921234" },
  BS: { pattern: "(###) ###-####", example: "2423591234" },
  DM: { pattern: "(###) ###-####", example: "7672251234" },
  DO: { pattern: "(###) ###-####", example: "8092001234" },
  GD: { pattern: "(###) ###-####", example: "4734031234" },
  GU: { pattern: "(###) ###-####", example: "6713001234" },
  JM: { pattern: "(###) ###-####", example: "8762101234" },
  KN: { pattern: "(###) ###-####", example: "8694651234" },
  KY: { pattern: "(###) ###-####", example: "3459491234" },
  LC: { pattern: "(###) ###-####", example: "7584511234" },
  MP: { pattern: "(###) ###-####", example: "6702341234" },
  MS: { pattern: "(###) ###-####", example: "6644911234" },
  PR: { pattern: "(###) ###-####", example: "7872001234" },
  TC: { pattern: "(###) ###-####", example: "6492311234" },
  TT: { pattern: "(###) ###-####", example: "8682011234" },
  VC: { pattern: "(###) ###-####", example: "7844301234" },
  VG: { pattern: "(###) ###-####", example: "2844941234" },
  VI: { pattern: "(###) ###-####", example: "3407121234" },
  AS: { pattern: "(###) ###-####", example: "6847331234" },

  // Americas - Caribbean (non-NANP)
  AW: { pattern: "### ####", example: "5601234" },
  BQ: { pattern: "### ####", example: "7181234" },
  BL: { pattern: "### ## ## ##", trunk: "0", example: "690123456" },
  BZ: { pattern: "###-####", example: "2221234" },
  CR: { pattern: "#### ####", example: "22123456" },
  CU: { pattern: "# ### ####", example: "712345678" },
  CW: { pattern: "### ####", example: "5181234" },
  GP: { pattern: "### ## ## ##", trunk: "0", example: "690123456" },
  GT: { pattern: "#### ####", example: "23456789" },
  HN: { pattern: "####-####", example: "22123456" },
  HT: { pattern: "## ## ####", example: "34101234" },
  MF: { pattern: "### ## ## ##", trunk: "0", example: "690123456" },
  MQ: { pattern: "### ## ## ##", trunk: "0", example: "696123456" },
  NI: { pattern: "#### ####", example: "81234567" },
  PA: { pattern: "####-####", example: "61234567" },
  SV: { pattern: "#### ####", example: "70123456" },
  SX: { pattern: "### ####", example: "5201234" },

  // Americas - South
  AR: { pattern: "## ####-####", trunk: "0", example: "1123456789" },
  BO: { pattern: "# #######", example: "212345678" },
  BR: { pattern: "(##) #####-####", trunk: "0", example: "11912345678" },
  CL: { pattern: "# #### ####", example: "912345678" },
  CO: { pattern: "### ### ####", example: "3211234567" },
  EC: { pattern: "## ### ####", trunk: "0", example: "991234567" },
  FK: { pattern: "#####", example: "51234" },
  GF: { pattern: "### ## ## ##", trunk: "0", example: "694123456" },
  GY: { pattern: "### ####", example: "6091234" },
  PE: { pattern: "### ### ###", example: "912345678" },
  PY: { pattern: "### ### ###", trunk: "0", example: "961123456" },
  SR: { pattern: "###-####", example: "7411234" },
  UY: { pattern: "## ### ###", trunk: "0", example: "94123456" },
  VE: { pattern: "###-#######", trunk: "0", example: "4121234567" },

  // Europe - Northern
  AX: { pattern: "## ### ## ##", trunk: "0", example: "412345678" },
  DK: { pattern: "## ## ## ##", example: "32123456" },
  EE: { pattern: "#### ####", example: "51234567" },
  FI: { pattern: "## ### ## ##", trunk: "0", example: "412345678" },
  FO: { pattern: "### ###", example: "211234" },
  GB: { pattern: "#### ### ####", trunk: "0", example: "7911123456" },
  GG: { pattern: "#### ######", trunk: "0", example: "7781123456" },
  IE: { pattern: "## ### ####", trunk: "0", example: "851234567" },
  IM: { pattern: "#### ######", trunk: "0", example: "7624123456" },
  IS: { pattern: "### ####", example: "6111234" },
  JE: { pattern: "#### ######", trunk: "0", example: "7797123456" },
  LT: { pattern: "(#) ### ## ##", trunk: "8", example: "61234567" },
  LV: { pattern: "## ### ###", example: "21234567" },
  NO: { pattern: "### ## ###", example: "41234567" },
  SE: { pattern: "##-### ## ##", trunk: "0", example: "701234567" },

  // Europe - Southern
  AD: { pattern: "### ###", example: "312345" },
  AL: { pattern: "## ### ####", trunk: "0", example: "672123456" },
  BA: { pattern: "## ###-###", trunk: "0", example: "61123456" },
  CY: { pattern: "## ######", example: "96123456" },
  ES: { pattern: "### ## ## ##", example: "612345678" },
  GI: { pattern: "### #####", example: "57123456" },
  GR: { pattern: "### ### ####", example: "6912345678" },
  HR: { pattern: "## ### ####", trunk: "0", example: "911234567" },
  IT: { pattern: "### ### ####", example: "3121234567" },
  MC: { pattern: "## ## ## ##", example: "61234567" },
  ME: { pattern: "## ### ###", trunk: "0", example: "67123456" },
  MK: { pattern: "## ### ###", trunk: "0", example: "72345678" },
  MT: { pattern: "#### ####", example: "96121234" },
  PT: { pattern: "### ### ###", example: "912345678" },
  RS: { pattern: "## ### ####", trunk: "0", example: "601234567" },
  SI: { pattern: "## ### ###", trunk: "0", example: "31123456" },
  SM: { pattern: "## ## ## ##", example: "66661212" },
  VA: { pattern: "## #### ####", example: "0612345678" },
  XK: { pattern: "## ### ###", trunk: "0", example: "43123456" },

  // Europe - Western
  AT: { pattern: "### ######", trunk: "0", example: "664123456" },
  BE: { pattern: "### ## ## ##", trunk: "0", example: "470123456" },
  CH: { pattern: "## ### ## ##", trunk: "0", example: "781234567" },
  DE: { pattern: "### ########", trunk: "0", example: "15112345678" },
  FR: { pattern: "# ## ## ## ##", trunk: "0", example: "612345678" },
  LI: { pattern: "### ## ##", example: "6601234" },
  LU: { pattern: "### ### ###", example: "621123456" },
  NL: { pattern: "# ## ## ## ##", trunk: "0", example: "612345678" },

  // Europe - Eastern
  BG: { pattern: "### ### ###", trunk: "0", example: "481234567" },
  BY: { pattern: "## ###-##-##", trunk: "80", example: "291234567" },
  CZ: { pattern: "### ### ###", example: "601123456" },
  GE: { pattern: "### ## ## ##", example: "555123456" },
  HU: { pattern: "## ### ####", trunk: "06", example: "201234567" },
  MD: { pattern: "#### ## ##", trunk: "0", example: "62112345" },
  PL: { pattern: "### ### ###", example: "512345678" },
  RO: { pattern: "### ### ###", trunk: "0", example: "721234567" },
  RU: { pattern: "(###) ###-##-##", trunk: "8", example: "9121234567" },
  SK: { pattern: "### ### ###", trunk: "0", example: "912123456" },
  UA: { pattern: "## ### ## ##", trunk: "0", example: "501234567" },

  // Africa - Northern
  DZ: { pattern: "### ## ## ##", trunk: "0", example: "551234567" },
  EG: { pattern: "## #### ####", trunk: "0", example: "1001234567" },
  EH: { pattern: "## #### ###", trunk: "0", example: "650123456" },
  LY: { pattern: "##-#######", trunk: "0", example: "911234567" },
  MA: { pattern: "##-### ## ##", trunk: "0", example: "650123456" },
  SD: { pattern: "## ### ####", trunk: "0", example: "911231234" },
  TN: { pattern: "## ### ###", example: "20123456" },

  // Africa - Western
  BJ: { pattern: "## ## ## ##", example: "90123456" },
  BF: { pattern: "## ## ## ##", example: "70123456" },
  CV: { pattern: "### ## ##", example: "9911234" },
  CI: { pattern: "## ## ## ## ##", example: "0102030405" },
  GM: { pattern: "### ####", example: "3012345" },
  GH: { pattern: "## ### ####", trunk: "0", example: "231234567" },
  GN: { pattern: "### ## ## ##", example: "601234567" },
  GW: { pattern: "### ####", example: "9551234" },
  LR: { pattern: "### ### ####", trunk: "0", example: "770123456" },
  ML: { pattern: "## ## ## ##", example: "65123456" },
  MR: { pattern: "## ## ## ##", example: "22123456" },
  NE: { pattern: "## ## ## ##", example: "93123456" },
  NG: { pattern: "### ### ####", trunk: "0", example: "8021234567" },
  SH: { pattern: "####", example: "2158" },
  SN: { pattern: "## ### ## ##", example: "701234567" },
  SL: { pattern: "## ######", example: "25123456" },
  TG: { pattern: "## ## ## ##", example: "90123456" },

  // Africa - Central
  AO: { pattern: "### ### ###", example: "923123456" },
  CF: { pattern: "## ## ## ##", example: "70012345" },
  CG: { pattern: "## ### ####", example: "061234567" },
  CD: { pattern: "### ### ###", example: "991234567" },
  CM: { pattern: "# ## ## ## ##", example: "671234567" },
  GA: { pattern: "# ## ## ##", example: "6012345" },
  GQ: { pattern: "### ### ###", example: "222123456" },
  ST: { pattern: "### ####", example: "9812345" },
  TD: { pattern: "## ## ## ##", example: "63012345" },

  // Africa - Eastern
  BI: { pattern: "## ## ## ##", example: "79123456" },
  KM: { pattern: "### ## ##", example: "3212345" },
  DJ: { pattern: "## ## ## ##", example: "77831234" },
  ER: { pattern: "# ### ###", example: "7123456" },
  ET: { pattern: "## ### ####", example: "911234567" },
  KE: { pattern: "### ######", trunk: "0", example: "712123456" },
  MG: { pattern: "## ## ### ##", trunk: "0", example: "321234567" },
  MW: { pattern: "# #### ####", trunk: "0", example: "991234567" },
  MU: { pattern: "#### ####", example: "52501234" },
  MZ: { pattern: "## ### ####", example: "821234567" },
  RE: { pattern: "### ## ## ##", trunk: "0", example: "692123456" },
  RW: { pattern: "### ### ###", example: "781234567" },
  SC: { pattern: "# ## ## ##", example: "2510123" },
  SO: { pattern: "## #######", example: "901234567" },
  SS: { pattern: "## ### ####", example: "977123456" },
  TZ: { pattern: "### ### ###", trunk: "0", example: "621234567" },
  UG: { pattern: "### ######", trunk: "0", example: "712345678" },
  YT: { pattern: "### ## ## ##", trunk: "0", example: "639012345" },
  ZM: { pattern: "## #######", trunk: "0", example: "955123456" },
  ZW: { pattern: "## ### ####", trunk: "0", example: "712345678" },

  // Africa - Southern
  BW: { pattern: "## ### ###", example: "71123456" },
  LS: { pattern: "#### ####", example: "50123456" },
  NA: { pattern: "## ### ####", trunk: "0", example: "811234567" },
  ZA: { pattern: "## ### ####", trunk: "0", example: "711234567" },
  SZ: { pattern: "## ## ####", example: "76171234" },

  // Middle East
  AE: { pattern: "## ### ####", trunk: "0", example: "501234567" },
  BH: { pattern: "#### ####", example: "36001234" },
  IR: { pattern: "### ### ####", trunk: "0", example: "9121234567" },
  IQ: { pattern: "### ### ####", example: "7901234567" },
  IL: { pattern: "##-###-####", trunk: "0", example: "501234567" },
  JO: { pattern: "# #### ####", example: "790123456" },
  KW: { pattern: "#### ####", example: "50012345" },
  LB: { pattern: "## ### ###", trunk: "0", example: "71123456" },
  OM: { pattern: "#### ####", example: "92123456" },
  PS: { pattern: "### ## ####", trunk: "0", example: "599123456" },
  QA: { pattern: "#### ####", example: "33123456" },
  SA: { pattern: "## ### ####", trunk: "0", example: "512345678" },
  SY: { pattern: "### ### ###", trunk: "0", example: "944123456" },
  TR: { pattern: "### ### ## ##", trunk: "0", example: "5012345678" },
  YE: { pattern: "### ### ###", example: "712345678" },

  // Central Asia
  AF: { pattern: "## ### ####", trunk: "0", example: "701234567" },
  AM: { pattern: "## ######", trunk: "0", example: "77123456" },
  AZ: { pattern: "## ### ## ##", trunk: "0", example: "401234567" },
  KZ: { pattern: "(###) ### ## ##", trunk: "8", example: "7011234567" },
  KG: { pattern: "### ### ###", trunk: "0", example: "700123456" },
  TJ: { pattern: "## ### ## ##", example: "917123456" },
  TM: { pattern: "## ## ## ##", trunk: "8", example: "66123456" },
  UZ: { pattern: "## ### ## ##", trunk: "8", example: "901234567" },

  // Asia - South
  BD: { pattern: "####-######", trunk: "0", example: "1812345678" },
  BT: { pattern: "## ## ## ##", example: "17123456" },
  IN: { pattern: "##### #####", trunk: "0", example: "9123456789" },
  LK: { pattern: "## # ######", trunk: "0", example: "712345678" },
  MV: { pattern: "###-####", example: "7912345" },
  NP: { pattern: "###-#######", example: "9841234567" },
  PK: { pattern: "### #######", trunk: "0", example: "3001234567" },

  // Asia - Southeast
  BN: { pattern: "### ####", example: "7121234" },
  KH: { pattern: "## ### ###", trunk: "0", example: "91234567" },
  ID: { pattern: "###-####-####", trunk: "0", example: "81234567890" },
  LA: { pattern: "## ## ### ###", trunk: "0", example: "2023451234" },
  MY: { pattern: "##-### ####", trunk: "0", example: "121234567" },
  MM: { pattern: "# ### ####", trunk: "0", example: "912345678" },
  PH: { pattern: "### ### ####", trunk: "0", example: "9171234567" },
  SG: { pattern: "#### ####", example: "81234567" },
  TH: { pattern: "##-###-####", trunk: "0", example: "812345678" },
  TL: { pattern: "#### ####", example: "77121234" },
  VN: { pattern: "## ### ## ##", trunk: "0", example: "912345678" },

  // Asia - East
  CN: { pattern: "### #### ####", trunk: "0", example: "13112345678" },
  HK: { pattern: "#### ####", example: "51234567" },
  JP: { pattern: "##-####-####", trunk: "0", example: "9012345678" },
  KP: { pattern: "### ### ####", example: "1921234567" },
  KR: { pattern: "##-####-####", trunk: "0", example: "1012345678" },
  MO: { pattern: "#### ####", example: "66123456" },
  MN: { pattern: "#### ####", example: "88123456" },
  TW: { pattern: "### ### ###", trunk: "0", example: "912345678" },

  // Oceania
  AU: { pattern: "### ### ###", trunk: "0", example: "412345678" },
  CK: { pattern: "## ###", example: "71234" },
  FJ: { pattern: "### ####", example: "7012345" },
  FM: { pattern: "### ####", example: "3501234" },
  KI: { pattern: "########", example: "72012345" },
  MH: { pattern: "###-####", example: "2351234" },
  NC: { pattern: "## ## ##", example: "751234" },
  NF: { pattern: "# #####", example: "381234" },
  NR: { pattern: "### ####", example: "5551234" },
  NU: { pattern: "####", example: "1234" },
  NZ: { pattern: "## ### ####", trunk: "0", example: "211234567" },
  PF: { pattern: "## ## ## ##", example: "87123456" },
  PG: { pattern: "#### ####", example: "70123456" },
  PN: { pattern: "## ### ####", trunk: "0", example: "211234567" },
  PW: { pattern: "### ####", example: "6201234" },
  SB: { pattern: "## #####", example: "7412345" },
  TK: { pattern: "####", example: "7290" },
  TO: { pattern: "### ####", example: "7715123" },
  TV: { pattern: "## ####", example: "901234" },
  VU: { pattern: "### ####", example: "5912345" },
  WF: { pattern: "## ## ##", example: "821234" },
  WS: { pattern: "## ####", example: "721234" },

  // Other / Special territories
  AQ: { pattern: "# #### ####", example: "412345678" },
  BV: { pattern: "### ## ###", example: "41234567" },
  CC: { pattern: "### ### ###", trunk: "0", example: "412345678" },
  CX: { pattern: "### ### ###", trunk: "0", example: "412345678" },
  GL: { pattern: "## ## ##", example: "221234" },
  GS: { pattern: "#####", example: "51234" },
  HM: { pattern: "### ### ###", trunk: "0", example: "412345678" },
  IO: { pattern: "### ####", example: "3801234" },
  PM: { pattern: "## ## ##", example: "551234" },
  SJ: { pattern: "### ## ###", example: "41234567" },
  TF: { pattern: "### ## ## ##", trunk: "0", example: "692012345" },
  UM: { pattern: "(###) ###-####", example: "2025551234" },
};

/**
 * Format a string of digits according to a country's phone pattern.
 * Returns the formatted string for display purposes.
 */
export function formatPhoneNumber(digits: string, countryCode: string): string {
  const fmt = PHONE_FORMATS[countryCode];
  if (!fmt) return digits;

  const pattern = fmt.pattern;
  let result = "";
  let di = 0;

  for (let pi = 0; pi < pattern.length && di < digits.length; pi++) {
    if (pattern[pi] === "#") {
      result += digits[di++];
    } else {
      result += pattern[pi];
    }
  }

  // Append any remaining digits beyond the pattern length
  if (di < digits.length) {
    result += digits.slice(di);
  }

  return result;
}

/**
 * Get the placeholder text from a country's phone format pattern.
 * Replaces '#' with the example digits or just the pattern shape.
 */
export function getPhonePlaceholder(countryCode: string): string {
  const fmt = PHONE_FORMATS[countryCode];
  if (!fmt) return "";

  if (fmt.example) {
    return formatPhoneNumber(fmt.example, countryCode);
  }

  return fmt.pattern;
}

/**
 * Get the maximum digit count expected for a country's phone format.
 */
export function getMaxDigits(countryCode: string): number {
  const fmt = PHONE_FORMATS[countryCode];
  if (!fmt) return 15;
  return (fmt.pattern.match(/#/g) || []).length;
}
